import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, FormHelperText, FormLabel, Group, Input, Token, Typography } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput } from 'data/generated/graphql';
import QueryRuleSchemaEditor from 'features/publication-settings/components/QueryRuleSchemaEditor';
import { GLOBAL_PUBLICATION_KEY, usePublicationSettings, useSavePublicationSetting } from 'hooks/publication-settings';
import { ROLE_TYPE } from 'hooks/roles/types';
import useRequireRoles from 'hooks/roles/useRequireRoles';
import SiteConfigPage from 'pages/site-config/shared/SiteConfigPage';
import styles from './styles.module.scss';

type TagInputSectionProps = {
  label: string;
  description: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  isReadOnly?: boolean;
};

const TagInputSection = ({
  label,
  description,
  values,
  onChange,
  placeholder,
  isReadOnly = false
}: TagInputSectionProps) => {
  const [inputValue, setInputValue] = useState('');

  const addValue = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeValue = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <Group gap="xs" direction="column" fullWidth>
      <FormLabel label={label} labelPosition="top" fullWidth />
      <FormHelperText>{description}</FormHelperText>
      <div className={styles.tagInput}>
        {values.map((v, i) => (
          <Token key={v} color="ice" onDismiss={isReadOnly ? undefined : () => removeValue(i)}>
            {v}
          </Token>
        ))}
        <Input
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addValue(inputValue)}
          placeholder={values.length === 0 ? placeholder : undefined}
          className={styles.tagInputField}
          disabled={isReadOnly}
        />
      </div>
      <FormHelperText>Press Enter or click away to add. Backspace removes the last item.</FormHelperText>
    </Group>
  );
};

function SiteConfigGlobalSettings() {
  useRequireRoles(ROLE_TYPE.ADMIN);

  const { data, isLoading, fetchStatus } = usePublicationSettings({ publicationKey: GLOBAL_PUBLICATION_KEY });
  const isFetching = isLoading && fetchStatus !== 'idle';

  const { alertError, alertSuccess } = useAlert();
  const { mutateAsync, isLoading: isSaving } = useSavePublicationSetting();

  const [queryRuleSchemaDraft, setQueryRuleSchemaDraft] = useState<Record<string, unknown>>({});
  const [defaultQueryRuleSchemaDraft, setDefaultQueryRuleSchemaDraft] = useState<Record<string, unknown>>({});
  const [liveCoverageSourceUrlsDraft, setLiveCoverageSourceUrlsDraft] = useState<string[]>([]);

  useEffect(() => {
    setQueryRuleSchemaDraft(data?.publicationSetting.queryRuleSchema ?? {});
    setDefaultQueryRuleSchemaDraft(data?.publicationSetting.defaultQueryRuleSchema ?? {});
    setLiveCoverageSourceUrlsDraft((data?.publicationSetting.liveCoverageSourceUrls as string[] | undefined) ?? []);
  }, [data]);

  const handleSave = async () => {
    if (!data?.publicationSetting) {
      alertError('Global publication settings record not found.');
      return;
    }
    const existing = data.publicationSetting;
    const input: PublicationSettingUpdateInput = {
      ...(existing as unknown as PublicationSettingUpdateInput),
      publicationKey: GLOBAL_PUBLICATION_KEY,
      queryRuleSchema: queryRuleSchemaDraft,
      defaultQueryRuleSchema: defaultQueryRuleSchemaDraft,
      liveCoverageSourceUrls: liveCoverageSourceUrlsDraft.length > 0 ? liveCoverageSourceUrlsDraft : null
    };
    try {
      await mutateAsync({ publicationSettingUpdateInput: input });
      alertSuccess('Global settings saved.');
    } catch (err) {
      alertError(`Failed to save: ${(err as Error).message}`);
    }
  };

  const handleCancel = () => {
    setQueryRuleSchemaDraft(data?.publicationSetting.queryRuleSchema ?? {});
    setDefaultQueryRuleSchemaDraft(data?.publicationSetting.defaultQueryRuleSchema ?? {});
    setLiveCoverageSourceUrlsDraft((data?.publicationSetting.liveCoverageSourceUrls as string[] | undefined) ?? []);
  };

  const actions = data ? (
    <>
      <Button tertiary onClick={handleCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button primary onClick={() => void handleSave()} disabled={isSaving}>
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </>
  ) : null;

  return (
    <SiteConfigPage title="Global" actions={actions}>
      {isFetching ? (
        <ResponsiveLoader size="xl" />
      ) : (
        <>
          <Typography margin={{ bottom: 'lg' }} variant="note">
            Cross-publication shared configuration.
          </Typography>

          <Group gap="lg" direction="column" fullWidth>
            <Box>
              <Box.Title>
                <span className={styles.bucketTitle}>Live Coverage Source URLs</span>
              </Box.Title>
              <Box.Content>
                <Typography margin={{ bottom: 'sm' }} variant="note">
                  Seed URLs whose live-coverage streams any publication can embed. URLs not listed here won&apos;t be
                  pickable in the live-coverage picker.
                </Typography>
                <TagInputSection
                  label="Live Coverage Source URLs"
                  description="Shared across all publications."
                  placeholder="E.g. https://www.wsj.com/livecoverage"
                  values={liveCoverageSourceUrlsDraft}
                  onChange={setLiveCoverageSourceUrlsDraft}
                />
              </Box.Content>
            </Box>

            <Box>
              <Box.Title>
                <span className={styles.bucketTitle}>Query rule schema</span>
              </Box.Title>
              <Box.Content>
                <Typography margin={{ bottom: 'sm' }} variant="note">
                  Master registry of Allesseh query rule keys available across all publications. Each entry defines the
                  key, an optional label, and whether it encodes as a text, date, or group term.
                </Typography>
                <QueryRuleSchemaEditor
                  data={queryRuleSchemaDraft}
                  onChange={(next) => setQueryRuleSchemaDraft(next)}
                  hideUnknownWarning
                />
              </Box.Content>
            </Box>

            <Box>
              <Box.Title>
                <span className={styles.bucketTitle}>Default query rule schema</span>
              </Box.Title>
              <Box.Content>
                <Typography margin={{ bottom: 'sm' }} variant="note">
                  Curated subset of the query rule schema shown to publications that have no per-pub schema configured.
                  Keys must exist in the query rule schema above.
                </Typography>
                <QueryRuleSchemaEditor
                  data={defaultQueryRuleSchemaDraft}
                  onChange={(next) => setDefaultQueryRuleSchemaDraft(next)}
                  globalQueryRuleSchema={queryRuleSchemaDraft}
                />
              </Box.Content>
            </Box>
          </Group>
        </>
      )}
    </SiteConfigPage>
  );
}

export default SiteConfigGlobalSettings;
